/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { createLinkFromLetter, QuotationBase, QuotationPrintMethod } from '@gh/builder-shared-data';
import { textMessageDialogRoute } from '@gh/common-internal-shared';

import { createDialogRoute, FormStateBuilder, getDialogInitData, MutablePageState } from '@gh/common-shared';
import { EntityVersion, ID, PrintEntryPoint } from '@gh/common-shared-data';
import { TranslationService } from '@gh/core-mls';
import {
  DialogInput,
  DialogService,
  FormMode,
  noAnswer,
  NotificationService,
  TextMessageDialogConfig,
  WaitingService,
  yesAnswer,
  yesNoCancelConfirmation,
  yesNoConfirmation,
} from '@gh/core-ui';
import { createDragulaDndOptions } from '@gh/core-ui/dragula';
import { filter$, switchMap$ } from '@gh/rx/operators';

import { assign, findIndex, first, isEmpty, isNil, pull } from 'lodash';

import { QuoteStructureBusinessService } from '../../../resources/quote-structure-business.service';
import {
  Heading,
  QuoteStructure,
  QuoteStructureItem,
  QuoteStructureSelectedObject,
} from '../../../resources/quote-structure.model';

export interface QuoteStructureConfig {
  sfId: ID;
  quoteId: ID;
  base: QuotationBase;
  version: EntityVersion;
}

export function getDraggingClass(element: HTMLElement): string {
  return element.classList.contains(ITEM_CLASS) ? ITEM_DRAGGING_CLASS : DETAIL_DRAGGING_CLASS;
}

const DETAIL_DRAGGING_CLASS = 'detail-dragging';
const ITEM_DRAGGING_CLASS = 'item-dragging';
const ITEM_CLASS = 'structure-item';

@Component({
  selector: 'gh-quote-structure-dialog',
  template: require('./quote-structure-dialog.component.html'),
  styles: [require('./quote-structure-dialog.component.scss')],
})

export class QuoteStructureDialogComponent implements OnInit, DialogInput<QuoteStructureConfig> {
  @ViewChild('quoteStructureDialog', { static: false }) _container: any;

  input: QuoteStructureConfig;
  form: MutablePageState<QuoteStructure>;
  quoteStructure: QuoteStructure;
  headingItemDragulaOptions = createDragulaDndOptions();
  headingDescriptionDragulaOptions = createDragulaDndOptions('dnd-pointer,detail-dnd-pointer');
  selectedVariations: ID[] = [];
  selectedObject: QuoteStructureSelectedObject;
  sfId: ID;
  quoteId: ID;

  get isHideAllIndeterminate(): boolean {
    let length = this.quoteStructure.headings.length;
    let countOfHided = this.quoteStructure.headings.filter((item) => item.hide)?.length;
    this.quoteStructure.headings.forEach((item) => {
      length += item.items.length;
      length += item.details.length;
      countOfHided += item.items.filter((subItem) => subItem.hide).length;
      countOfHided += item.details.filter((subItem) => subItem.hide).length;
    });

    return countOfHided > 0 && countOfHided < length;
  }

  get isPriceAllIndeterminate(): boolean {
    let countOfPriceChecked = 0;
    this.quoteStructure.headings.forEach((item) =>
                                           item.items.forEach((subItem) => {
                                             if (subItem.showPrice) {
                                               countOfPriceChecked++;
                                             }
                                           }));
    return countOfPriceChecked > 0  && countOfPriceChecked < this.countOfSubItemsWithParam('showPrice');
  }
  get isUOMAllIndeterminate(): boolean {
    let countOfPriceChecked = 0;
    this.quoteStructure.headings.forEach((item) =>
                                           item.items.forEach((subItem) => {
                                             if (subItem.showUom) {
                                               countOfPriceChecked++;
                                             }
                                           }));
    return countOfPriceChecked > 0  && countOfPriceChecked < this.countOfSubItemsWithParam('showUom');
  }

  get isQTYAllIndeterminate(): boolean {
    let countOfPriceChecked = 0;
    this.quoteStructure.headings.forEach((item) =>
                                           item.items.forEach((subItem) => {
                                             if (subItem.showQty) {
                                               countOfPriceChecked++;
                                             }
                                           }));
    return countOfPriceChecked > 0  && countOfPriceChecked < this.countOfSubItemsWithParam('showQty');
  }

  constructor(private dialogRef: MatDialogRef<any>,
              private formStateBuilder: FormStateBuilder,
              private waitingService: WaitingService,
              private notificationService: NotificationService,
              @Inject(MAT_DIALOG_DATA) private dialogData: any,
              private businessService: QuoteStructureBusinessService,
              private dialogService: DialogService,
              private translationService: TranslationService) {
  }

  get isEmpty(): boolean {
    return this.quoteStructure.headings.length === 0;
  }

  get isItemSelected(): boolean {
    return this.selectedObject && !isNil(this.selectedObject.item);
  }

  get isHeadingSelected(): boolean {
    return this.selectedObject && !isNil(this.selectedObject.heading);
  }

  get container(): HTMLElement {
    return this._container.elementRef.nativeElement;
  }

  ngOnInit(): void {
    assign(this, getDialogInitData(this.dialogData));
    this.initPage();
    this.form = new MutablePageState(FormMode.EditMode);
  }

  get hideAllChecked(): boolean {
    return !!this.quoteStructure.headings.length &&
      !this.quoteStructure.headings.find((head) => !head.hide ||
        !!head.items.find((item) => !item.hide) ||
        !!head.details.find((item) => !item.hide),
      );
  }

  get allPriceChecked(): boolean {
    return !!this.countOfSubItemsWithParam('showPrice') &&
      !this.quoteStructure.headings.find((head) => !!head.items.find((item) => !item.showPrice));
  }

  get allUOMChecked(): boolean {
    return !!this.countOfSubItemsWithParam('showUom') &&
      !this.quoteStructure.headings.find((head) => !!head.items.find((item) => !item.showUom));
  }

  get allQTYChecked(): boolean {
   return !!this.countOfSubItemsWithParam('showQty') &&
     !this.quoteStructure.headings.find((head) => !!head.items.find((item) => !item.showQty));
  }

  countOfSubItemsWithParam(paramName: string): number {
    let count = 0;
    this.quoteStructure.headings.forEach((head) => {
      if (head.items.length) {
        count += head.items.filter((item) => item.hasOwnProperty(paramName)).length;
      }
    });
    return count;
  }

  initPage(): void {
    if (!isEmpty(this.quoteStructure.variations)) {
      // tslint:disable-next-line:no-non-null-assertion
      this.selectedVariations.push(first(this.quoteStructure.variations)!.id);
    }

    if (this.quoteStructure.headings) {
      this.selectedObject = {
        heading: first(this.quoteStructure.headings)!, // tslint:disable-line:no-non-null-assertion
      };
    }
  }

  addItemsToStructure(): void {
    const heading = this.selectedObject.heading ? this.selectedObject.heading : first(this.quoteStructure.headings);

    this.selectedVariations.forEach((id) => {
      const index = findIndex(this.quoteStructure.variations, (item: QuoteStructureItem) => item.id === id);

      if (index > -1) {
        const item = first(this.quoteStructure.variations.splice(index, 1));

        heading!.items.push(item!); // tslint:disable-line:no-non-null-assertion
        this.form.setDirty();
      }
    });

    this.selectedVariations = [];
  }

  onDropElement([_, el]: [any, HTMLElement]): void {
    // tslint:disable-next-line:no-non-null-assertion
    this.container!.classList.remove(getDraggingClass(el));
    this.removeDetailsFromVariations();
    this.form.setDirty();
  }

  onDragElement([_, el]: [any, HTMLElement]): void {
    // tslint:disable-next-line:no-non-null-assertion
    this.container!.classList.add(getDraggingClass(el));
  }

  onApply(): void {
    if (this.quoteStructure.variations.length > 0) {
      const confirmation$ = this.dialogService.confirmation(yesNoConfirmation('msg_quote_structure_apply'));

      confirmation$.pipe(filter$(yesAnswer)).subscribe(() => this.saveQuoteStructure());
      confirmation$.pipe(filter$(noAnswer)).subscribe(() => this.dialogRef.close());
    } else {
      this.saveQuoteStructure();
    }
  }

  onHeadingChange(heading: Heading): void {
    this.form.setDirty();
  }

  onCancel(): void {
    if (this.form.dirty) {
      const confirmation$ = this.dialogService.confirmation(yesNoCancelConfirmation('msg_unsaved_info'));

      confirmation$.pipe(filter$(yesAnswer)).subscribe(() => this.onApply());
      confirmation$.pipe(filter$(noAnswer)).subscribe(() => this.dialogRef.close());
    } else {
      this.dialogRef.close();
    }
  }

  onItemSelectChange(selected: boolean, item: QuoteStructureItem): void {
    if (selected) {
      this.selectedVariations.push(item.id);
    } else {
      pull(this.selectedVariations, item.id);
    }
    this.form.setDirty();
  }

  onSelected(selectedObjects: QuoteStructureSelectedObject): void {
    this.selectedObject = selectedObjects;
  }

  removeItemsFromStructure(): void {
    if (this.selectedObject && !this.selectedObject.item) {
      return;
    }

    // tslint:disable-next-line:no-non-null-assertion
    const index = this.selectedObject.heading.items.indexOf(this.selectedObject.item!);

    if (index > -1) {
      this.selectedObject.heading.items.splice(index, 1);
      this.quoteStructure.variations.push(this.selectedObject.item!); // tslint:disable-line:no-non-null-assertion
      this.selectedObject.item = void 0;
      this.removeDetailsFromVariations();
    }
  }

  saveQuoteStructure(): void {
    this.businessService.save(this.sfId, this.quoteId, this.quoteStructure).pipe(
      this.waitingService.operator())
      .subscribe(
        (structure: QuoteStructure) => this.dialogRef.close(structure),
        this.notificationService.processAll);
  }

  removeDetailsFromVariations(): void {
    this.quoteStructure.variations.forEach((variation) => {
      if (variation.details.length > 0) {
        variation.details = [];
        this.form.setDirty();
      }
    });
  }

  onRefresh(): void {
    if (this.form.dirty) {
      const confirmation$ = this.dialogService.confirmation(yesNoCancelConfirmation('msg_quote_structure_refresh'));

      confirmation$.pipe(
        filter$(yesAnswer),
        switchMap$(() =>
          this.businessService.save(this.sfId, this.quoteId, this.quoteStructure).pipe(
            this.waitingService.operator())))
        .subscribe(
          () => {
            this.refresh();
          },
          this.notificationService.processAll);

      confirmation$.pipe(filter$(noAnswer))
        .subscribe(() => {
          this.refresh();
        });
    } else {
      this.refresh();
    }
  }

  refresh(): void {
    this.businessService.refresh(this.quoteId).pipe(
      this.waitingService.operator())
      .subscribe(
        (quoteStructure) => {
          assign(this, { quoteStructure });
          this.initPage();
          this.form.resetDirty();
        },
        this.notificationService.processAll);
  }

  onPreview(): void {
    this.businessService.preview({
      salesFolderId: this.sfId,
      quotationId: this.quoteId,
      type: this.input.base,
      entryPoint: PrintEntryPoint.Preview,
      isAuthorityToProceedRequired: false,
      isForEstimator: false,
      isShowBasePrice: false,
      isPrintTakeOff: false,
      version: this.input.version,
      headings: this.quoteStructure.headings,
      method: QuotationPrintMethod.PrintQuote,
    }).pipe(
      this.waitingService.operator())
      .subscribe(
        ({ quoteDoc }) => this.dialogService.openWithInput(textMessageDialogRoute, <TextMessageDialogConfig<string>>{
          title: this.translationService.get('lbl_link_to_preview_quote_document'),
          html: this.translationService.get('lbl_text_preview_quote_document', {
            url: createLinkFromLetter(quoteDoc),
          }),
          buttons: [{ value: 'ok', title: this.translationService.get('lbl_ok'), type: 'primary' }],
        }),
        this.notificationService.processAll);
  }

  onAllClick(event: any, param: string): void {
    event.stopPropagation();
    event.preventDefault();
    let checked = false;
    switch (param) {
      case 'showQty':
        checked = !this.allQTYChecked;
        break;
      case 'showUom':
        checked = !this.allUOMChecked;
        break;
      case 'showPrice':
        checked = !this.allPriceChecked;
        break;
      case 'hide':
        checked = !this.hideAllChecked;
        break;
      default:
        break;
    }
    this.quoteStructure.headings.forEach((item) => {
      if (param === 'hide') {
        item.hide = checked;
        item.details?.forEach((subItem) => {
          subItem.hide = checked;
        });
      }
      item.items?.forEach((subItem) => {
        subItem[param] = checked;
      });
    });
  }
}

export const quoteStructureDialogRoute = createDialogRoute({
  component: QuoteStructureDialogComponent,
  init: QuoteStructureBusinessService,
  config: {
    minWidth: '1000px',
    width: '90vw',
    maxWidth: '90vw',
    height: '90vh',
    disableClose: true,
  },
});
